import React from 'react';
import { cn } from '../../../utils/cn';
import Tooltip from '../../ui/Tooltip';
import type { BrokerBreakdownEntry } from '../../../types/schedule';

interface Props {
  items: BrokerBreakdownEntry[];
  /** Optional extra classes for the wrapper. */
  className?: string;
}

/**
 * Renders the per-broker effective-lots breakdown for a leg or strategy
 * (e.g. `ZA1234 · 10L  ·  AN5678 · 15L`). Renders nothing when there are no
 * allocations so callers can render unconditionally.
 *
 * The per-broker number is `lot_multiplier × leg.lots` (or strategy.total_lots)
 * — i.e. the actual lots that broker will place at execution time. Hovering
 * the line surfaces the full broker · client · multiplier · effective form.
 */
export const BrokerBreakdownLine: React.FC<Props> = ({ items, className }) => {
  if (!items || items.length === 0) return null;
  const tooltip = items
    .map((b) => `${b.broker} · ${b.client_id} · ${b.lot_multiplier}x = ${b.effective_lots}L`)
    .join('  ·  ');
  return (
    <Tooltip text={tooltip}>
      <span
        className={cn(
          'text-[10px] text-gray-400 dark:text-gray-500 tabular-nums',
          className,
        )}
      >
        {items.map((b, i) => (
          <React.Fragment key={`${b.client_id}-${i}`}>
            {i > 0 && <span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>}
            <span>
              {b.client_id} · {b.effective_lots}L
            </span>
          </React.Fragment>
        ))}
      </span>
    </Tooltip>
  );
};

export default BrokerBreakdownLine;